import type { PaletteColor, SimplePaletteColorOptions } from "@mui/material";
import type { ComponentsOverrides } from "@mui/material/styles";
import { darkGreyScale, greyScale } from "shared/Palette";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import type {
	CalloutAppearance,
	CalloutColor,
	CalloutVariant,
} from "web_myplann/Shared/Types/CalloutAppearance";
export const Colors = {
	Primary: "#00C0AA",
	Primary75: "#52D4C5",
	Primary25: "#A3E8E0",
	Primary300: "#E7F6FC",

	Secondary: "#009CEA",
	Secondary75: "#52BCF1",
	Secondary50: "#7ACCF4",
	Secondary25: "#A3DBF7",
	Secondary10: "#CCEBFB",

	Success: "#90CC11",

	SuccessAccent: "#16A34A",
	SuccessAccent25: "#DCFCE7",

	Warning: "#FACC15",
	Warning25: "#FDEDAB",

	Danger: "#E01A49",
	Danger75: "#EA6383",
	Danger50: "#EF88A0",

	MonoChrome50: "#F8FAFC",
	MonoChrome100: "#F1F5F9",
	MonoChrome200: "#E2E8F0",
	MonoChrome300: "#CBD5E1",
	MonoChrome400: "#94A3B8",
	MonoChrome500: "#64748B",
	MonoChrome600: "#475569",
	MonoChrome700: "#334155",
	MonoChrome800: "#1E293B",

	White: "#FFFFFF",
	Black: "#0F172A",

	/** @deprecated Use the `orange` palette scale (`theme.palette.orange.main` / `theme.palette.orange[600]`). */
	Orange: "#F97316",
	/** @deprecated Use the `orange` palette scale. */
	Orange75: "#FBA061",
	/** @deprecated Use the `orange` palette scale. */
	Orange25: "#FDCDAB",

	/** @deprecated Use the `blue` palette scale (`theme.palette.blue.main` / `theme.palette.blue[600]`). */
	Blue: "#3565DC",
	/** @deprecated Use the `blue` palette scale. */
	Blue75: "#5D84E3",
	/** @deprecated Use the `blue` palette scale. */
	Blue25: "#AEC1F1",

	/** @deprecated Use the `purple` palette scale (`theme.palette.purple.main` / `theme.palette.purple[600]`). */
	Purple: "#A855F7",
	/** @deprecated Use the `purple` palette scale. */
	Purple75: "#C48BFA",
	/** @deprecated Use the `purple` palette scale. */
	Purple25: "#E0C2FC",

	DashboardGradientTotalFrom: "#FA674E",
	DashboardGradientTotalTo: "#F18E29",
	DashboardGradientInvestmentFrom: "#CC70F7",
	DashboardGradientInvestmentTo: "#F572D5",
	DashboardGradientInvestmentPropertyFrom: "#292D74",
	DashboardGradientInvestmentPropertyTo: "#16B8F3",
	DashboardGradientCashFrom: "#01343A",
	DashboardGradientCashTo: "#00844B",
	DashboardGradientOtherFrom: "#112244",
	DashboardGradientOtherTo: "#24547D",
};

export const DarkModeColors = {
	Primary: "#00C0AA",
	Primary75: "#52D4C5",
	Primary25: "#A3E8E0",
	Primary300: "#E7F6FC",

	Secondary: "#009CEA",
	Secondary75: "#52BCF1",
	Secondary50: "#7ACCF4",
	Secondary25: "#A3DBF7",
	Secondary10: "#CCEBFB",

	Success: "#90CC11",

	SuccessAccent: "#16A34A",
	SuccessAccent25: "#DCFCE7",

	Warning: "#FACC15",
	Warning25: "#FDEDAB",

	Danger: "#E01A49",
	Danger75: "#EA6383",
	Danger50: "#EF88A0",

	MonoChrome50: "#1E293B",
	MonoChrome100: "#334155",
	MonoChrome200: "#475569",
	MonoChrome300: "#64748B",
	MonoChrome400: "#94A3B8",
	MonoChrome500: "#CBD5E1",
	MonoChrome600: "#E2E8F0",
	MonoChrome700: "#F1F5F9",
	MonoChrome800: "#F8FAFC",

	White: "#0F172A",
	Black: "#FFFFFF",

	/** @deprecated Use the `orange` palette scale (`theme.palette.orange.main` / `theme.palette.orange[600]`). */
	Orange: "#F97316",
	/** @deprecated Use the `orange` palette scale. */
	Orange75: "#FBA061",
	/** @deprecated Use the `orange` palette scale. */
	Orange25: "#FDCDAB",

	/** @deprecated Use the `blue` palette scale (`theme.palette.blue.main` / `theme.palette.blue[600]`). */
	Blue: "#3565DC",
	/** @deprecated Use the `blue` palette scale. */
	Blue75: "#5D84E3",
	/** @deprecated Use the `blue` palette scale. */
	Blue25: "#AEC1F1",

	/** @deprecated Use the `purple` palette scale (`theme.palette.purple.main` / `theme.palette.purple[600]`). */
	Purple: "#A855F7",
	/** @deprecated Use the `purple` palette scale. */
	Purple75: "#C48BFA",
	/** @deprecated Use the `purple` palette scale. */
	Purple25: "#E0C2FC",

	DashboardGradientTotalFrom: "#FA674E",
	DashboardGradientTotalTo: "#F18E29",
	DashboardGradientInvestmentFrom: "#CC70F7",
	DashboardGradientInvestmentTo: "#F572D5",
	DashboardGradientInvestmentPropertyFrom: "#292D74",
	DashboardGradientInvestmentPropertyTo: "#16B8F3",
	DashboardGradientCashFrom: "#01343A",
	DashboardGradientCashTo: "#00844B",
	DashboardGradientOtherFrom: "#112244",
	DashboardGradientOtherTo: "#24547D",
};

export type ColorScale = {
	50: string;
	100: string;
	200: string;
	300: string;
	400: string;
	500: string;
	600: string;
	700: string;
	800: string;
	900: string;
};

export type ColorScales = {
	grey: ColorScale;
	blue: ColorScale;
	green: ColorScale;
	amber: ColorScale;
	orange: ColorScale;
	red: ColorScale;
	purple: ColorScale;
	indigo: ColorScale;
	teal: ColorScale;
	yellow: ColorScale;
	pink: ColorScale;
};

/**
 * Design-system color scales (50–900). These are the canonical theme colors;
 * the matching MUI palette entries (grey/blue/orange/purple/green/amber/red/indigo)
 * are built from these in `Shared/Themes.ts` and expose both `.main` and `[500]`.
 */
export const Scales: ColorScales = {
	grey: greyScale,
	blue: {
		50: "#E3F2FD",
		100: "#BBDEFB",
		200: "#90CAF9",
		300: "#64B5F6",
		400: "#42A5F5",
		500: "#2196F3",
		600: "#1E88E5",
		700: "#1976D2",
		800: "#1565C0",
		900: "#0D47A1",
	},
	green: {
		50: "#E8F5E9",
		100: "#C8E6C9",
		200: "#A5D6A7",
		300: "#81C784",
		400: "#66BB6A",
		500: "#4CAF50",
		600: "#43A047",
		700: "#388E3C",
		800: "#2E7D32",
		900: "#1B5E20",
	},
	amber: {
		50: "#FFF8E1",
		100: "#FFECB3",
		200: "#FFE082",
		300: "#FFD54F",
		400: "#FFCA28",
		500: "#FFC107",
		600: "#FFA000",
		700: "#FF8F00",
		800: "#FF6F00",
		900: "#E65100",
	},
	orange: {
		50: "#FFF3E0",
		100: "#FFE0B2",
		200: "#FFCC80",
		300: "#FFB74D",
		400: "#FFA726",
		500: "#FB8C00",
		600: "#F57C00",
		700: "#EF6C00",
		800: "#E65100",
		900: "#BF360C",
	},
	red: {
		50: "#FFEBEE",
		100: "#FFCDD2",
		200: "#EF9A9A",
		300: "#E57373",
		400: "#EF5350",
		500: "#F44336",
		600: "#E53935",
		700: "#D32F2F",
		800: "#C62828",
		900: "#B71C1C",
	},
	purple: {
		50: "#F3E5F5",
		100: "#E1BEE7",
		200: "#CE93D8",
		300: "#BA68C8",
		400: "#AB47BC",
		500: "#9C27B0",
		600: "#8E24AA",
		700: "#7B1FA2",
		800: "#6A1B9A",
		900: "#4A148C",
	},
	indigo: {
		50: "#E8EAF6",
		100: "#C5CAE9",
		200: "#9FA8DA",
		300: "#7986CB",
		400: "#5C6BC0",
		500: "#3F51B5",
		600: "#3949AB",
		700: "#303F9F",
		800: "#283593",
		900: "#1A237E",
	},
	teal: {
		50: "#F0FDFA",
		100: "#CCFBF1",
		200: "#99F6E4",
		300: "#5EEAD4",
		400: "#2DD4BF",
		500: "#14B8A6",
		600: "#0D9488",
		700: "#0F766E",
		800: "#115E59",
		900: "#134E4A",
	},
	yellow: {
		50: "#FEFCE8",
		100: "#FEF9C3",
		200: "#FEF08A",
		300: "#FDE047",
		400: "#FACC15",
		500: "#EAB308",
		600: "#CA8A04",
		700: "#A16207",
		800: "#854D0E",
		900: "#713F12",
	},
	pink: {
		50: "#FDF2F8",
		100: "#FCE7F3",
		200: "#FBCFE8",
		300: "#F9A8D4",
		400: "#F472B6",
		500: "#EC4899",
		600: "#DB2777",
		700: "#BE185D",
		800: "#9D174D",
		900: "#831843",
	},
};

/**
 * Dark-mode counterpart of {@link Scales}. The neutral `grey` ramp is inverted
 * (graphite floor → near-white ink) so anything keyed on `grey` — `text.primary`,
 * the baked typography colors, surfaces — flips under the same key in dark mode.
 * Colored tints (50/100/200) become dark hue-tuned surfaces; the accents
 * (300–900) keep their hue but are desaturated ~15% so vivid brand/status
 * colors don't scream against the dark surfaces.
 */
export const DarkScales: ColorScales = {
	grey: darkGreyScale,
	blue: {
		50: "#15324A",
		100: "#1B3E59",
		200: "#265575",
		300: "#7AB3E1",
		400: "#5EA3DC",
		500: "#4090D2",
		600: "#3480C6",
		700: "#2D70B3",
		800: "#26609E",
		900: "#1D4882",
	},
	green: {
		50: "#163523",
		100: "#1E442E",
		200: "#295C3B",
		300: "#8ABE8E",
		400: "#71AF77",
		500: "#5CA261",
		600: "#509255",
		700: "#448049",
		800: "#396F3E",
		900: "#29572E",
	},
	amber: {
		50: "#332611",
		100: "#433318",
		200: "#5C4820",
		300: "#F1CF6E",
		400: "#ECC250",
		500: "#E9B635",
		600: "#E49D29",
		700: "#DA8A21",
		800: "#CE6F1B",
		900: "#B25616",
	},
	orange: {
		50: "#33240F",
		100: "#432F14",
		200: "#5C401C",
		300: "#EBAD61",
		400: "#E59C43",
		500: "#DF8728",
		600: "#D5761E",
		700: "#C9671A",
		800: "#B95116",
		900: "#973B17",
	},
	red: {
		50: "#371A1E",
		100: "#472125",
		200: "#612D32",
		300: "#DA8281",
		400: "#DA6764",
		500: "#D9574F",
		600: "#CF4C49",
		700: "#BA403E",
		800: "#A83534",
		900: "#952928",
	},
	purple: {
		50: "#2A1A3A",
		100: "#37234A",
		200: "#4B3062",
		300: "#B67DC2",
		400: "#A762B5",
		500: "#9948A8",
		600: "#8C409E",
		700: "#793790",
		800: "#682F82",
		900: "#4D2470",
	},
	indigo: {
		50: "#1C1E45",
		100: "#262A5C",
		200: "#343A72",
		300: "#818BC5",
		400: "#6773B6",
		500: "#4E5CA7",
		600: "#46529B",
		700: "#3C478B",
		800: "#323C7C",
		900: "#252C67",
	},
	teal: {
		50: "#0D2E2A",
		100: "#CCFBF1",
		200: "#1C5048",
		300: "#5EEAD4",
		400: "#2DD4BF",
		500: "#14B8A6",
		600: "#0D9488",
		700: "#0F766E",
		800: "#115E59",
		900: "#134E4A",
	},
	yellow: {
		50: "#2E2A07",
		100: "#FEF9C3",
		200: "#544D0E",
		300: "#FDE047",
		400: "#FACC15",
		500: "#EAB308",
		600: "#CA8A04",
		700: "#A16207",
		800: "#854D0E",
		900: "#713F12",
	},
	pink: {
		50: "#2E0D1E",
		100: "#FCE7F3",
		200: "#561A38",
		300: "#F9A8D4",
		400: "#F472B6",
		500: "#EC4899",
		600: "#DB2777",
		700: "#BE185D",
		800: "#9D174D",
		900: "#831843",
	},
};

export type SurfaceTokens = {
	paper: string;
	appBar: string;
	accent: string;
	/** Surface raised above paper (shadows read poorly in dark, so it lightens instead). */
	elevated: string;
	divider: string;
};

export const Surfaces: SurfaceTokens = {
	paper: "#FFFFFF",
	appBar: "#FFFFFF",
	accent: "#E3F2FD",
	elevated: "#FFFFFF",
	divider: "#F5F5F5",
};

export const DarkSurfaces: SurfaceTokens = {
	paper: "#28282C",
	appBar: "#2C2C30",
	accent: "#1B3047",
	elevated: "#34343A",
	divider: "#3A3A40",
};

export type PortfolioAreaColor = { tint: string; ink: string };

export type PortfolioAreaColors = Record<
	ClientPortfolioDomainEnum | "property",
	PortfolioAreaColor
>;

export const PortfolioAreaPalette: PortfolioAreaColors = {
	[ClientPortfolioDomainEnum.LifeInsurance]: {
		tint: "#D9F0C0",
		ink: "#2F6B18",
	},
	[ClientPortfolioDomainEnum.PropertyInsurance]: {
		tint: "#C8E6C9",
		ink: "#1B5E20",
	},
	[ClientPortfolioDomainEnum.PensionProducts]: {
		tint: "#FEF9C3",
		ink: "#A16207",
	},
	[ClientPortfolioDomainEnum.BuildingSavings]: {
		tint: "#FFE0B2",
		ink: "#A82F0A",
	},
	[ClientPortfolioDomainEnum.Investments]: { tint: "#C5CAE9", ink: "#303F9F" },
	[ClientPortfolioDomainEnum.BusinessLoans]: {
		tint: "#E1BEE7",
		ink: "#7B1FA2",
	},
	[ClientPortfolioDomainEnum.Loans]: { tint: "#BBDEFB", ink: "#0D47A1" },
	[ClientPortfolioDomainEnum.Leasing]: { tint: "#CFFAFE", ink: "#0E7490" },
	[ClientPortfolioDomainEnum.Commodities]: { tint: "#CCFBF1", ink: "#0F766E" },
	[ClientPortfolioDomainEnum.BankingProducts]: {
		tint: "#FCE7F3",
		ink: "#BE185D",
	},
	[ClientPortfolioDomainEnum.Cryptocurrencies]: {
		tint: "#EDE9FE",
		ink: "#6D28D9",
	},
	property: { tint: "#FAE8FF", ink: "#A21CAF" },
	[ClientPortfolioDomainEnum.Services]: { tint: "#FFECB3", ink: "#E65100" },
	[ClientPortfolioDomainEnum.PaidAdvisory]: { tint: "#FFE4E6", ink: "#BE123C" },
};

export const DarkPortfolioAreaPalette: PortfolioAreaColors = {
	[ClientPortfolioDomainEnum.LifeInsurance]: {
		tint: "#22421A",
		ink: "#A8DC88",
	},
	[ClientPortfolioDomainEnum.PropertyInsurance]: {
		tint: "#1E442E",
		ink: "#7CC47F",
	},
	[ClientPortfolioDomainEnum.PensionProducts]: {
		tint: "#413516",
		ink: "#E8C74A",
	},
	[ClientPortfolioDomainEnum.BuildingSavings]: {
		tint: "#432F14",
		ink: "#FFA94D",
	},
	[ClientPortfolioDomainEnum.Investments]: { tint: "#262A5C", ink: "#8A95D6" },
	[ClientPortfolioDomainEnum.BusinessLoans]: {
		tint: "#37234A",
		ink: "#C77DD6",
	},
	[ClientPortfolioDomainEnum.Loans]: { tint: "#1B3E59", ink: "#6FB7F2" },
	[ClientPortfolioDomainEnum.Leasing]: { tint: "#12394A", ink: "#62D2E8" },
	[ClientPortfolioDomainEnum.Commodities]: { tint: "#10403B", ink: "#5CD8C2" },
	[ClientPortfolioDomainEnum.BankingProducts]: {
		tint: "#451F35",
		ink: "#F08BC0",
	},
	[ClientPortfolioDomainEnum.Cryptocurrencies]: {
		tint: "#2F2553",
		ink: "#A99BF5",
	},
	property: { tint: "#3A1B40", ink: "#E9A5F2" },
	[ClientPortfolioDomainEnum.Services]: { tint: "#433318", ink: "#FFCA47" },
	[ClientPortfolioDomainEnum.PaidAdvisory]: { tint: "#4A1F2B", ink: "#F591A2" },
};

export type HspThemeColors = typeof Colors & {
	hspColor?: string;
	hspColor75?: string;
	hspColor25?: string;
};

/** A MUI palette color (`light/main/dark/contrastText`) that also carries the full 50–900 scale. */
type ScaledPaletteColor = PaletteColor & ColorScale;
type ScaledPaletteColorOptions = SimplePaletteColorOptions &
	Partial<ColorScale>;

/**
 * Pre-design-system color values, preserved only so existing screens keep their
 * current look during the migration. The canonical palette entries now hold the new
 * design-system colors (`success` = green, `warning` = amber, `error` = red,
 * `info` = blue, `grey` = the warm neutral scale); migrate `palette.deprecated.*`
 * references over to those.
 */
type DeprecatedPalette = {
	// success/warning mirror the original partial definitions (no light/dark);
	// error/info were fully defined, so they get the strict PaletteColor type.
	success: SimplePaletteColorOptions;
	warning: SimplePaletteColorOptions;
	error: PaletteColor;
	info: PaletteColor;
	grey: ColorScale;
};

interface PropsColorOverrides {
	orange: true;
	neutral: true;
}

interface PropsBadgeColorOverrides {
	transparent: true;
}

type ThemeExtendedProps = {
	colors: {
		border: {
			solid: (color: string) => string;
			main: string;
			hover: string;
			active: string;
			flatRowDivider: string;
		};
		placeholder: string;
		glassBackground: string;
		tabActiveBackground: string;
	};
	shadow: {
		primary: string;
		primaryHover: string;
		blueGlow: string;
		elevated: string;
		floating: string;
	};
	overlayScrollbar: {
		width: number;
		inset: number;
		color: string;
		radius: number;
		fadeDuration: number;
	};
	gradients: {
		primaryHero: string;
		realization: string;
		premium: string;
		avatar: string;
		dashboardSky: string;
		dashboardSkyGlow: string;
		iconBadge: string;
	};
	hero: {
		pillBg: string;
		tileBg: string;
		divider: string;
	};
	transition: {
		fast: string;
		slow: string;
	};
	borderRadius: string;
	radii: {
		none: string;
		sm: string;
		md: string;
		lg: string;
		xl: string;
		full: string;
	};
	iconSize: {
		sm: number;
		md: number;
		lg: number;
	};
	iconBoxSize: {
		sm: number;
		md: number;
	};
	callout: Record<CalloutVariant, (color: CalloutColor) => CalloutAppearance>;
	navigation: {
		header: {
			height: number;
		};
	};
	datePanel: {
		cellHeight: number;
		gridHeight: number;
		columnGap: string;
		paddingBottom: string;
		weekdayLabel: {
			height: number;
			fontSize: string;
		};
		disabledArrowOpacity: number;
	};
	activity: {
		eventRow: {
			titleLines: {
				list: number;
				preview: number;
			};
			chevronOffset: number;
		};
	};
	notificationCenter: {
		unreadDot: {
			size: number;
			inset: number;
		};
		unreadTintOpacity: number;
		rowLineHeight: number;
		emptyIconBoxSize: number;
	};
	inspiration: {
		section: {
			cardGap: string;
			dragThreshold: number;
			paddingBottom: number;
			paddingLeft: number;
			endSpacerWidth: number;
			cursor: string;
		};
		card: {
			width: number;
			height: number;
			borderRadius: string;
			padding: number;
			activeTransform: string;
			overlayGradient: string;
			titleLineHeight: number;
			textShadow: string;
		};
		seeAll: {
			gap: number;
			iconSize: number;
			iconGlyphSize: number;
			iconBorderRadius: string;
		};
		hero: {
			height: number;
			overlayGradient: string;
		};
	};
	dashboard: {
		gradients: {
			total: { from: string; to: string };
			investment: { from: string; to: string };
			investmentProperty: { from: string; to: string };
			cash: { from: string; to: string };
			other: { from: string; to: string };
		};
		quickCalculator: {
			tints: Record<
				"blue" | "orange" | "green" | "purple" | "rose",
				{ background: string; icon: string }
			>;
		};
	};
	chat: {
		message: {
			maxWidth: string;
			gap: number;
			paddingX: number;
			paddingY: number;
			lineHeight: number;
			pendingOpacity: number;
			marginBottom: {
				grouped: string;
				last: string;
			};
			borderRadius: {
				default: string;
				tight: string;
			};
		};
		avatar: {
			size: number;
			gap: number;
		};
		sender: {
			background: string;
			attachmentBackground: string;
			textColor: string;
		};
		recipient: {
			background: string;
			textColor: string;
			elevation: number;
		};
		timestamp: {
			inset: number;
		};
	};
	vh: (value: number) => string;
	timeline: {
		height: {
			small: string;
			medium: string;
			large: string;
		};
	};
	spacingTokens: {
		none: number;
		xxs: number;
		xs: number;
		sm: number;
		md: number;
		lg: number;
		xl: number;
		xxl: number;
	};
	bottomSpacing: {
		nativeBottomBar: string;
		stickyCta: string;
		sheet: string;
	};
};

interface TextDarkModeViabaleOptions {
	white: string;
	black: string;
}

declare module "@mui/material" {
	interface TypeBackground {
		appBar: string;
	}
	interface ButtonPropsColorOverrides extends PropsColorOverrides {}
	interface IconButtonPropsColorOverrides extends PropsColorOverrides {}
	interface CheckboxPropsColorOverrides extends PropsColorOverrides {}
	interface ChipPropsColorOverrides extends PropsColorOverrides {}
	interface ChipPropsVariantOverrides {
		tag: true;
		pill: true;
		filter: true;
		performance: true;
		trend: true;
	}
	interface BadgePropsColorOverrides extends PropsBadgeColorOverrides {}
	interface BadgePropsVariantOverrides {
		headerGradient: true;
		headerFlat: true;
		button: true;
	}
	interface PaletteOptions {
		blue: ScaledPaletteColorOptions;
		orange: ScaledPaletteColorOptions;
		purple: ScaledPaletteColorOptions;
		green: ScaledPaletteColorOptions;
		amber: ScaledPaletteColorOptions;
		red: ScaledPaletteColorOptions;
		indigo: ScaledPaletteColorOptions;
		teal: ScaledPaletteColorOptions;
		yellow: ScaledPaletteColorOptions;
		pink: ScaledPaletteColorOptions;
		realization: PaletteOptions["primary"];
		myPLANN: MyPlannPaletteOptions;
		surface: { muted: string; border: string };
		portfolioArea: PortfolioAreaColors;
		darkmodeText: TextDarkModeViabaleOptions;
		/** @deprecated Legacy colors kept for migration — prefer the canonical palette entries. */
		deprecated: DeprecatedPalette;
	}

	export interface MyPlannPaletteOptions {
		main: string;
		main75: string;
		main50: string;
		main25: string;
		main10: string;
	}

	interface Palette {
		blue: ScaledPaletteColor;
		orange: ScaledPaletteColor;
		purple: ScaledPaletteColor;
		green: ScaledPaletteColor;
		amber: ScaledPaletteColor;
		red: ScaledPaletteColor;
		indigo: ScaledPaletteColor;
		teal: ScaledPaletteColor;
		yellow: ScaledPaletteColor;
		pink: ScaledPaletteColor;
		realization: PaletteColor;
		myPLANN: MyPlannPaletteOptions;
		surface: { muted: string; border: string };
		portfolioArea: PortfolioAreaColors;
		darkmodeText: TextDarkModeViabaleOptions;
		/** @deprecated Legacy colors kept for migration — prefer the canonical palette entries. */
		deprecated: DeprecatedPalette;
	}
	interface ThemeOptions extends ThemeExtendedProps {}
}

declare module "@mui/material/styles" {
	interface TypographyVariants {
		displayMd: React.CSSProperties;
		sectionLabel: React.CSSProperties;
		subtitle3: React.CSSProperties;
		hint: React.CSSProperties;
		bodyStrong: React.CSSProperties;
		bodyMedium: React.CSSProperties;
		value: React.CSSProperties;
		deprecatedH1: React.CSSProperties;
		deprecatedH2: React.CSSProperties;
		deprecatedH3: React.CSSProperties;
		deprecatedH4: React.CSSProperties;
		deprecatedSubtitle1: React.CSSProperties;
		deprecatedSubtitle2: React.CSSProperties;
		deprecatedBody1: React.CSSProperties;
		deprecatedBody2: React.CSSProperties;
		deprecatedBodyStrong: React.CSSProperties;
		deprecatedBodyMedium: React.CSSProperties;
		deprecatedCaption: React.CSSProperties;
		deprecatedHint: React.CSSProperties;
	}
	interface TypographyVariantsOptions {
		displayMd?: React.CSSProperties;
		sectionLabel?: React.CSSProperties;
		subtitle3?: React.CSSProperties;
		hint?: React.CSSProperties;
		bodyStrong?: React.CSSProperties;
		bodyMedium?: React.CSSProperties;
		value?: React.CSSProperties;
		deprecatedH1?: React.CSSProperties;
		deprecatedH2?: React.CSSProperties;
		deprecatedH3?: React.CSSProperties;
		deprecatedH4?: React.CSSProperties;
		deprecatedSubtitle1?: React.CSSProperties;
		deprecatedSubtitle2?: React.CSSProperties;
		deprecatedBody1?: React.CSSProperties;
		deprecatedBody2?: React.CSSProperties;
		deprecatedBodyStrong?: React.CSSProperties;
		deprecatedBodyMedium?: React.CSSProperties;
		deprecatedCaption?: React.CSSProperties;
		deprecatedHint?: React.CSSProperties;
	}
	interface Theme extends ThemeExtendedProps {}
	interface ComponentNameToClassKey {
		SmallDrawer: "handle" | "divider";
	}
	interface Components<Theme = unknown> {
		SmallDrawer?: {
			styleOverrides?: ComponentsOverrides<Theme>["SmallDrawer"];
		};
	}
}

declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		displayMd: true;
		sectionLabel: true;
		subtitle3: true;
		hint: true;
		bodyStrong: true;
		bodyMedium: true;
		value: true;
		deprecatedH1: true;
		deprecatedH2: true;
		deprecatedH3: true;
		deprecatedH4: true;
		deprecatedSubtitle1: true;
		deprecatedSubtitle2: true;
		deprecatedBody1: true;
		deprecatedBody2: true;
		deprecatedBodyStrong: true;
		deprecatedBodyMedium: true;
		deprecatedCaption: true;
		deprecatedHint: true;
	}
	interface TypographyOwnProps {
		tabular?: boolean;
	}
}

declare module "@mui/material/InputBase" {
	interface InputBaseProps {
		variant?: "pill";
	}
}

declare module "@mui/material/Menu" {
	interface MenuProps {
		/** Opts the menu into the redesigned surface (see `MuiMenu` variants). */
		redesign?: boolean;
	}
}

declare module "@mui/material/Slider" {
	interface SliderOwnProps<Value extends number | number[]> {
		variant?: "flat";
	}
}

declare module "@mui/material/Tabs" {
	interface TabsOwnProps {
		redesigned?: boolean;
		deprecatedUnderline?: boolean;
	}
}

declare module "@mui/material/Tab" {
	interface TabOwnProps {
		redesigned?: boolean;
	}
}

declare module "@mui/material/Switch" {
	interface SwitchProps {
		redesigned?: boolean;
	}
}

declare module "@mui/material/Divider" {
	interface DividerOwnProps {
		redesigned?: boolean;
	}
}

declare module "@mui/material/ToggleButtonGroup" {
	interface ToggleButtonGroupProps {
		segmented?: boolean;
	}
}

declare module "@mui/material/ToggleButton" {
	interface ToggleButtonOwnProps {
		segmented?: boolean;
	}
}

declare module "@mui/material/LinearProgress" {
	interface LinearProgressProps {
		slim?: boolean;
	}
}

declare module "@mui/material/Alert" {
	interface AlertPropsVariantOverrides {
		notice: true;
	}
}

declare module "@mui/material/Button" {
	interface ButtonPropsVariantOverrides {
		primary: true;
		secondary: true;
		tertiary: true;
		ghost: true;
		textLink: true;
		inlineAction: true;
		listCta: true;
		showAll: true;
		disclosure: true;
		addRow: true;
		deprecatedListRow: true;
		deprecatedHeaderAction: true;
		alertCta: true;
		alertCtaText: true;
	}
}

declare module "@mui/material/IconButton" {
	interface IconButtonPropsVariantOverrides {
		solid: true;
		soft: true;
		ghost: true;
		header: true;
		fab: true;
	}
	interface IconButtonOwnProps {
		variant?: keyof IconButtonPropsVariantOverrides;
	}
}

declare module "@mui/material/Avatar" {
	interface AvatarPropsVariantOverrides {
		softTeal: true;
		softGreen: true;
		softOrange: true;
		softBlue: true;
		softPurple: true;
		softGrey: true;
	}
}
